import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@/shared/ui/Modal'
import { Button } from '@/shared/ui/Button'
import { Textarea } from '@/shared/ui/Input'
import { Avatar } from '@/shared/ui/Avatar'
import { DirhamAmount } from '@/shared/ui/Dirham'
import { toast } from '@/store/useToastStore'
import { getApiError } from '@/api/client'
import { useEditWalletBalance } from '@/api/hooks'
import { cn, localized } from '@/shared/utils'
import { ArrowUpCircle, ArrowDownCircle } from 'lucide-react'

export interface WalletStudent {
  id: number
  name?: string | null
  name_ar?: string | null
  photo_url?: string | null
  wallet_balance?: number | string | null
}

// Manual wallet credit/debit by an admin:
// POST v2/admin/wallet/edit-balance { action, amount, reason, user_id }
export function EditWalletBalanceModal({ open, onClose, student }: {
  open: boolean; onClose: () => void; student: WalletStudent | null
}) {
  const { t, i18n } = useTranslation()
  const [action, setAction] = useState<'credit' | 'debit'>('credit')
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const editBalance = useEditWalletBalance()

  useEffect(() => {
    if (open) { setAction('credit'); setAmount(''); setReason(''); editBalance.reset() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, student?.id])

  if (!student) return null

  const name = localized(student, 'name', i18n.language)
  const currentBalance = Number(student.wallet_balance ?? 0)
  const amountNum = Number(amount) || 0
  const newBalance = action === 'credit' ? currentBalance + amountNum : currentBalance - amountNum

  const handleClose = () => onClose()

  const submit = () => {
    if (!amountNum || amountNum <= 0) { toast.error(t('studentWallet.modal.toastAmount')); return }
    if (!reason.trim()) { toast.error(t('studentWallet.modal.toastReason')); return }
    editBalance.mutate(
      { action, amount: amountNum, reason: reason.trim(), user_id: student.id },
      {
        onSuccess: () => {
          toast.success(action === 'credit' ? t('studentWallet.modal.toastCredited') : t('studentWallet.modal.toastDebited'))
          handleClose()
        },
        onError: (e) => toast.error(getApiError(e)),
      },
    )
  }

  return (
    <Modal open={open} onClose={handleClose} title={t('studentWallet.modal.title')} size="sm">
      <div className="space-y-4">
        <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
          <Avatar name={name} src={student.photo_url} size="md" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-gray-900 truncate">{name}</p>
            <p className="text-xs text-gray-400">{t('studentWallet.modal.currentBalance')}</p>
          </div>
          <DirhamAmount value={currentBalance} size={14} />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setAction('credit')}
            className={cn(
              'flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm font-bold transition-colors',
              action === 'credit' ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
            )}
          >
            <ArrowUpCircle size={16} /> {t('studentWallet.modal.credit')}
          </button>
          <button
            type="button"
            onClick={() => setAction('debit')}
            className={cn(
              'flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm font-bold transition-colors',
              action === 'debit' ? 'bg-red-500 border-red-500 text-white' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
            )}
          >
            <ArrowDownCircle size={16} /> {t('studentWallet.modal.debit')}
          </button>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{t('studentWallet.modal.amount')}</label>
          <div className="relative">
            <span className="absolute start-3 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400 pointer-events-none">AED</span>
            <input
              type="number" min="0" step="0.01" inputMode="decimal"
              value={amount} onChange={e => setAmount(e.target.value)}
              placeholder="0.00"
              className="w-full ps-12 pe-3 py-2.5 text-sm font-semibold border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400"
            />
          </div>
        </div>

        {amountNum > 0 && (
          <div className={cn('rounded-xl p-3 text-center', newBalance < 0 ? 'bg-red-50' : action === 'credit' ? 'bg-green-50' : 'bg-gray-100')}>
            <span className="text-xs text-gray-500">{t('studentWallet.modal.newBalance')}: </span>
            <span className={cn('text-sm font-black', newBalance < 0 ? 'text-red-600' : action === 'credit' ? 'text-green-600' : 'text-gray-700')}>
              AED {newBalance.toLocaleString('en-AE', { maximumFractionDigits: 2 })}
            </span>
            {newBalance < 0 && <p className="text-[11px] text-red-500 mt-0.5">{t('studentWallet.modal.negativeWarning')}</p>}
          </div>
        )}

        <Textarea
          label={t('studentWallet.modal.reason')}
          className="min-h-20"
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder={t('studentWallet.modal.reasonPlaceholder')}
        />

        <div className="flex items-center justify-end gap-3">
          <Button variant="outline" onClick={handleClose} disabled={editBalance.isPending}>{t('common.cancel', 'Cancel')}</Button>
          <Button
            onClick={submit}
            loading={editBalance.isPending}
            className={action === 'credit' ? 'bg-green-500 hover:bg-green-600 border-transparent' : 'bg-red-500 hover:bg-red-600 border-transparent'}
          >
            {action === 'credit' ? t('studentWallet.modal.confirmCredit') : t('studentWallet.modal.confirmDebit')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
